import { FileUploadState } from '../../sidebar/DirectFileUploader';

/**
 * Proof the operator can pass on when closing a task — anything the user
 * could check without taking our word for it. Mirrors the backend's accepted
 * kinds (dream-be `ai-agents/lib/actionProof.ts`). Several can apply at once
 * (a reference AND the person who confirmed it, two screenshots), so this is
 * a set of tick-boxes, not a single choice. "Nothing to attach" is the only
 * exclusive one. The time for "spoke to someone" is stamped by the server
 * from the call log or the close — never typed here.
 */
export interface ProofSelection {
  screenshots: { on: boolean; keys: string[] };
  reference: { on: boolean; ref: string; issuer: string };
  person: { on: boolean; name: string };
  /** Confirmation emails found in the user's inbox, by provider message id. */
  email: { on: boolean; ids: string[] };
  nothing: { on: boolean; reason: string };
}

export const EMPTY_PROOF_SELECTION: ProofSelection = {
  screenshots: { on: false, keys: [] },
  reference: { on: false, ref: '', issuer: '' },
  person: { on: false, name: '' },
  email: { on: false, ids: [] },
  nothing: { on: false, reason: '' },
};

/**
 * An email the backend matcher judged to confirm this task (from
 * GET /human-tasks/:id/confirmation-emails). The operator only ever sees
 * these matches, never the inbox.
 */
export interface ConfirmationEmailCandidate {
  messageId: string;
  mailbox: string;
  from: string;
  subject: string;
  receivedAt: string;
  snippet: string;
  matchProbability: number;
}

export interface ConfirmationEmailSearch {
  status: 'idle' | 'loading' | 'ready' | 'error';
  candidates: ConfirmationEmailCandidate[];
  /** Why the search could not run, when the backend says so. */
  unavailable?: string;
}

export const EMPTY_EMAIL_SEARCH: ConfirmationEmailSearch = {
  status: 'idle',
  candidates: [],
};

/** An image the operator can tick as a screenshot. */
export interface ProofImageCandidate {
  key: string;
  /** Backend file reference, once the image exists server-side. */
  url?: string;
  name: string;
  /** Already posted in the task chat, or attached on this form. */
  source: 'chat' | 'attachment';
  status: 'uploading' | 'success' | 'error';
  /** Local object URL for a fresh attachment. */
  previewUrl?: string;
}

export interface ChatImage {
  url: string;
  postedBy: 'operator' | 'assistant';
}

export function proofImageCandidates(
  chatImages: ChatImage[],
  files: FileUploadState[],
): ProofImageCandidate[] {
  const fromChat: ProofImageCandidate[] = chatImages.map((img, i) => ({
    key: `chat:${img.url}`,
    url: img.url,
    name: `Task chat image ${i + 1}`,
    source: 'chat',
    status: 'success',
  }));
  const fromAttachments: ProofImageCandidate[] = files
    .filter((f) => f.type.startsWith('image/'))
    .map((f) => ({
      key: `file:${f.id}`,
      url: f.url,
      name: f.name,
      source: 'attachment',
      status: f.status,
      previewUrl: f.previewUrl,
    }));
  return [...fromChat, ...fromAttachments];
}

export interface CloseProofPayload {
  kind: 'screenshot' | 'reference' | 'person_time' | 'email' | 'none';
  ref: string | null;
  detail?: Record<string, string>;
}

export function hasAnyProofTicked(sel: ProofSelection): boolean {
  return (
    sel.screenshots.on ||
    sel.reference.on ||
    sel.person.on ||
    sel.email.on ||
    sel.nothing.on
  );
}

/** The proofs to send, or undefined when nothing was ticked. */
export function buildProofPayloads(
  sel: ProofSelection,
  images: ProofImageCandidate[],
  emails: ConfirmationEmailCandidate[] = [],
): CloseProofPayload[] | undefined {
  const out: CloseProofPayload[] = [];
  if (sel.screenshots.on) {
    for (const key of sel.screenshots.keys) {
      const img = images.find((i) => i.key === key);
      if (!img?.url) continue;
      out.push({
        kind: 'screenshot',
        ref: img.url,
        detail: { takenBy: 'team' },
      });
    }
  }
  if (sel.reference.on && sel.reference.ref.trim()) {
    out.push({
      kind: 'reference',
      ref: sel.reference.ref.trim(),
      ...(sel.reference.issuer.trim() && {
        detail: { issuer: sel.reference.issuer.trim() },
      }),
    });
  }
  if (sel.person.on && sel.person.name.trim()) {
    out.push({
      kind: 'person_time',
      ref: null,
      detail: { name: sel.person.name.trim() },
    });
  }
  if (sel.email.on) {
    for (const id of sel.email.ids) {
      const email = emails.find((e) => e.messageId === id);
      if (!email) continue;
      out.push({
        kind: 'email',
        ref: email.messageId,
        detail: {
          from: email.from,
          subject: email.subject,
          receivedAt: email.receivedAt,
          mailbox: email.mailbox,
          matchedBy: 'operator',
        },
      });
    }
  }
  if (sel.nothing.on && out.length === 0) {
    out.push({
      kind: 'none',
      ref: null,
      detail: { reason: sel.nothing.reason.trim() },
    });
  }
  return out.length > 0 ? out : undefined;
}

/** Screenshot refs the operator ticked that were posted in the chat (not attached here). */
export function tickedChatScreenshotUrls(
  sel: ProofSelection,
  images: ProofImageCandidate[],
): string[] {
  if (!sel.screenshots.on) return [];
  return images
    .filter(
      (i) =>
        i.source === 'chat' && i.url && sel.screenshots.keys.includes(i.key),
    )
    .map((i) => i.url!);
}

/** Why the ticked proof cannot be submitted yet, or null when it is complete. */
export function proofValidationError(
  sel: ProofSelection,
  images: ProofImageCandidate[],
  emails: ConfirmationEmailSearch = EMPTY_EMAIL_SEARCH,
): string | null {
  if (sel.screenshots.on) {
    const picked = images.filter((i) => sel.screenshots.keys.includes(i.key));
    if (picked.length === 0)
      return 'Tick at least one screenshot, or untick Screenshots.';
    if (picked.some((i) => i.status !== 'success' || !i.url))
      return 'Wait for the screenshot to finish uploading.';
  }
  if (sel.reference.on && !sel.reference.ref.trim())
    return 'Enter the reference number.';
  if (sel.person.on && !sel.person.name.trim())
    return 'Enter who confirmed it.';
  if (sel.email.on) {
    if (emails.status === 'loading')
      return "Wait while we check the user's inbox.";
    // No match yet is fine: a confirmation that arrives later is attached
    // automatically. A match left unticked is not — say which one it is.
    if (
      emails.status === 'ready' &&
      emails.candidates.length > 0 &&
      !sel.email.ids.some((id) =>
        emails.candidates.some((e) => e.messageId === id),
      )
    )
      return 'Tick the email that confirms it, or untick Confirmation email.';
  }
  if (sel.nothing.on && !sel.nothing.reason.trim())
    return "Say briefly why there's nothing to attach.";
  return null;
}
